import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatabaseZap } from "lucide-react";
import { Branding } from "@/context/branding-context";
import { useSession, signIn } from "@/lib/auth-client"

export function Login({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { data: session, isPending } = useSession();
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Keep these functions for future re-enablement of email login
  // const handleEmailLogin = async () => { ... }

  const handleGoogleLogin = async () => {
    try {
      // Use better-auth client for OAuth login
      await signIn.social({
        provider: 'google',
      });
    } catch (error) {
      console.error("OAuth login failed:", error);
      setLoginError("Failed to initiate Google login. Please make sure you've allowed the popup and try again.");
    }
  };

  const handleGitHubLogin = async () => {
    try {
      await signIn.social({
        provider: 'github',
      });
    } catch (error) {
      console.error("OAuth login failed:", error);
      setLoginError("Failed to initiate GitHub login. Please make sure you've allowed the popup and try again.");
    }
  };

  const handleLINELogin = async () => {
    try {
      await signIn.social({
        provider: 'line',
      });
    } catch (error) {
      console.error("OAuth login failed:", error);
      setLoginError("Failed to initiate LINE login. Please make sure you've allowed the popup and try again.");
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (session?.user) {
      navigate('/console', { replace: true });
    }
  }, [session, navigate]);

  if (isPending) {
    return (
      <div className={cn("flex min-h-svh w-full items-center justify-center p-6 md:p-10", className)}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex min-h-svh w-full items-center justify-center p-6 md:p-10", className)} {...props}>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-1">
            <DatabaseZap className="inline-block w-6 h-6" /> 
            <span className="text-2xl uppercase font-bold">{Branding.appName}</span>
          </CardTitle>
          <CardDescription>
            {Branding.requiredEmailDomain} emails required
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <Button
                type="button"
                className="w-full"
                onClick={handleGoogleLogin}
                variant="outline"
              >
                Login with Google
              </Button>

              <Button
                type="button"
                className="w-full"
                onClick={handleGitHubLogin}
                variant="outline"
              >
                Login with GitHub
              </Button>

              <Button
                type="button"
                className="w-full"
                onClick={handleLINELogin}
                variant="outline"
              >
                Login with LINE
              </Button>
            </div>

            {/* TODO: Re-enable email & password login form */}
            {/* <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                />
              </div>

              <Button
                type="button"
                className="w-full"
                onClick={handleEmailLogin}
                disabled={loading || !email || !password}
              >
                {loading ? 'Signing in...' : 'Sign in with Email'}
              </Button>

              {loginError && (
                <div className="text-red-500 text-sm text-center">
                  {loginError}
                </div>
              )}
            </div> */}
            
            <div className="text-center text-sm text-muted-foreground">
              Don't have an account? 
              <button 
                type="button" 
                onClick={() => navigate('/register')}
                className="ml-1 font-medium text-primary hover:underline"
              >
                Sign up
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;